import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StreakBadgeProgress } from "@/components/creator/streak/streak-badge-progress"
import { StreakFreezeCard } from "@/components/creator/streak/streak-freeze-card"
import { StreakFreezeAction } from "@/components/creator/streak/streak-freeze-dialog"
import { StreakHero } from "@/components/creator/streak/streak-hero"
import { StreakStateCard } from "@/components/creator/streak/streak-state-card"
import { StreakWeekStrip } from "@/components/creator/streak/streak-week-strip"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { creatorMissionsPath, creatorProfilePath } from "@/routes"
import type { CreatorStreakProps } from "@/types/streak"

/**
 * OKS-515 — §12 Streak / momentum detail (`/streak`).
 *
 * Page composition (UX Spec §2 — reading order = visual order):
 *   1. Back link → profile + a plain "Streak" heading (brand identity is
 *      carried by the shell-global brand strip since OKS-701)
 *   2. <StreakHero> — per-state flame/snowflake hero
 *   3. Body:
 *        - active  → THIS WEEK strip + freeze surface + badge-progress
 *        - at_risk → one state card ("Don't lose your N-day streak" + CTAs,
 *                    incl. the OKS-516 live freeze action / disabled guard)
 *        - frozen  → one state card ("Streak Freeze active · frozen until …")
 *        - broken  → one state card ("Start a fresh streak" + CTA)
 *
 * The server emits exactly one of active / at_risk / frozen / broken. As of
 * OKS-516 `frozen` is data-reachable (a successful freeze sets `frozen_until`
 * and `StatusResolver` emits `:frozen`). `loading` is handled by Inertia
 * in-flight navigation (the previous page stays painted), not a prop state.
 *
 * # Brand isolation
 *
 * Every prop is scoped server-side to `Current.membership` (the per-brand
 * streak row + this week's submissions) — no client query, no cross-brand
 * surface. Switching brand context re-renders via a full Inertia visit.
 *
 * Crew rules: js-routes for all URLs (`creatorMissionsPath` /
 * `creatorProfilePath`), no shadcn Form, CSS-only motion.
 */
export default function CreatorStreak({
  state,
  current_streak_days,
  longest_streak_days,
  hours_to_midnight,
  this_week,
  next_streak_badge,
  streak_freeze,
}: CreatorStreakProps) {
  const { t, i18n } = useTranslation()

  const frozenUntilLabel = streak_freeze.frozen_until
    ? new Date(streak_freeze.frozen_until).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "long",
      })
    : ""

  return (
    <AppLayout>
      <Head title={t("streak.detail.title")} />

      <main className="mx-auto flex w-full max-w-[480px] flex-col gap-5 px-4 py-5">
        <Link
          href={creatorProfilePath()}
          className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
          data-testid="streak-back"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          {t("streak.detail.back")}
        </Link>

        {/* OKS-701 — brand identity now lives in the shell-global brand
            strip (below the navbar on every creator page), so this page
            self-labels with a plain heading instead of the old
            "{Brand} · Streak" chrome. Real <h1> for reading order / a11y. */}
        <h1
          data-testid="streak-heading"
          className="text-lg font-semibold tracking-tight"
        >
          {t("streak.detail.brand_label")}
        </h1>

        <div className="overflow-hidden rounded-2xl">
          <StreakHero
            state={state}
            days={current_streak_days}
            longestDays={longest_streak_days}
            hoursToMidnight={hours_to_midnight}
          />
        </div>

        {state === "active" && (
          <>
            <StreakWeekStrip days={this_week} />
            <StreakFreezeCard freeze={streak_freeze} />
            {next_streak_badge && (
              <StreakBadgeProgress badge={next_streak_badge} />
            )}
          </>
        )}

        {state === "at_risk" && (
          <StreakStateCard
            testId="streak-at-risk-card"
            title={t("streak.detail.cards.at_risk.title", {
              count: current_streak_days,
            })}
            body={t("streak.detail.cards.at_risk.body")}
            actions={
              <>
                <Button asChild>
                  <Link
                    href={creatorMissionsPath()}
                    data-testid="streak-at-risk-post"
                  >
                    {t("streak.detail.cards.at_risk.post")}
                  </Link>
                </Button>
                {/* OKS-516 — live freeze action (confirm dialog) or a
                    disabled CTA + reason helper (none-left / insufficient). */}
                <StreakFreezeAction
                  freeze={streak_freeze}
                  days={current_streak_days}
                />
              </>
            }
          />
        )}

        {state === "frozen" && (
          <StreakStateCard
            testId="streak-frozen-card"
            title={t("streak.detail.cards.frozen.title")}
            body={t("streak.detail.cards.frozen.body", {
              date: frozenUntilLabel,
            })}
            actions={
              <Button asChild>
                <Link
                  href={creatorProfilePath()}
                  data-testid="streak-frozen-cta"
                >
                  {t("streak.detail.cards.frozen.cta")}
                </Link>
              </Button>
            }
          />
        )}

        {state === "broken" && (
          <StreakStateCard
            testId="streak-broken-card"
            title={t("streak.detail.cards.broken.title")}
            body={t("streak.detail.cards.broken.body")}
            actions={
              <Button asChild>
                <Link
                  href={creatorMissionsPath()}
                  data-testid="streak-broken-cta"
                >
                  {t("streak.detail.cards.broken.cta")}
                </Link>
              </Button>
            }
          />
        )}
      </main>
    </AppLayout>
  )
}
